import React, { useEffect, useState, useRef } from "react";
import { Row, Col } from "react-bootstrap";

import { useDispatch, useSelector } from "react-redux";
import { fetchLatestPermissions } from "slices/auth/login/thunk";
import { fetchWarehouseDropdownAll } from "slices/Master/Warehouse/thunk";
import {
  DashboardFilters,
  WarehouseOption,
} from "./Components/DashboardFilters";
import { DispatchSummary } from "./Components/DispatchSummary";
import { EnquirySummary } from "./Components/EnquirySummary";
import { InventorySummary } from "./Components/InventorySummary";
import { OrderFlowTracker } from "./Components/OrderFlowTracker";
import { OrderSummary } from "./Components/OrderSummary";
import { QuickActions } from "./Components/QuickActions";
import { QuotationSummary } from "./Components/QuotationSummary";
import { RevenueBreakdown } from "./Components/RevenueBreakdown";
import { RevenueByDeliveryStatus } from "./Components/RevenueByDeliveryStatus";
import { TopMetrics } from "./Components/TopMetrics";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import { app_modules } from "helpers/apps_modules_list";

// Helper function to check if user has dashboard permissions
const hasDashboardPermission = (): boolean => {
  try {
    const latestPermissions = JSON.parse(
      localStorage.getItem("userPermissions") || "[]"
    );

    const app_permissions = latestPermissions.find(
      (app: any) => app.apps_name === app_modules?.dashboard?.app_name
    );

    if (!app_permissions) {
      return false;
    }
    const dashboard_permissions = app_permissions.modules_permissions?.find(
      (module: any) =>
        module.modules_name === app_modules?.dashboard?.modules?.dashboard
    );

    if (!dashboard_permissions) {
      return false;
    }
    return !!dashboard_permissions.permissions.View;
  } catch (error) {
    console.error("Error checking dashboard permissions:", error);
    return false;
  }
};

const Dashboard = () => {
  document.title = "Dashboard | Inventory and Assest Management";
  const dispatch = useDispatch<any>();
  const { loading: topMetricsLoading } = useSelector((state: any) => state.TopMetricsReducer);

  // Permission state - check localStorage immediately for initial state
  const [hasPermission, setHasPermission] = useState<boolean>(() =>
    hasDashboardPermission()
  );
  const [permissionsLoaded, setPermissionsLoaded] = useState<boolean>(() => {
    // Check if permissions exist in localStorage
    const permissions = localStorage.getItem("userPermissions");
    return !!permissions;
  });

  // Filter state management
  const [dateFrom, setDateFrom] = useState<Date | undefined>(
    new Date(new Date().setDate(new Date().getDate() - 30))
  );
  const [dateTo, setDateTo] = useState<Date | undefined>(new Date());
  const [warehouse, setWarehouse] = useState<WarehouseOption[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<WarehouseOption[]>(
    []
  );

  // Ref for the end of sticky range (after QuotationSummary)
  const stickyEndRef = useRef<HTMLDivElement>(null);

  //*// Fetch latest permissions on component mount
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        await dispatch(fetchLatestPermissions()).unwrap();
        // Check permissions after fetching
        setHasPermission(hasDashboardPermission());
        setPermissionsLoaded(true);
      } catch (error) {
        console.error("Failed to fetch latest permissions", error);
        // Check permissions even if fetch fails (might be in localStorage)
        setHasPermission(hasDashboardPermission());
        setPermissionsLoaded(true);
      }
    };
    fetchPermissions();
  }, [dispatch]);

  // Fetch warehouse options
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const response = await dispatch(fetchWarehouseDropdownAll()).unwrap();
        setWarehouseOptions(response?.data || []);
      } catch (error) {
        console.error("Error fetching warehouse options:", error);
        setWarehouseOptions([]);
      }
    };
    fetchWarehouses();
  }, [dispatch]);

  // Handle filter reset
  const handleResetFilters = () => {
    setDateFrom(new Date(new Date().setDate(new Date().getDate() - 30)));
    setDateTo(new Date());
    // Reset to empty array (default state = all warehouses)
    setWarehouse([]);
  };

  // Show welcome message if user doesn't have dashboard permissions
  if (permissionsLoaded && !hasPermission) {
    return (
      <ListingPageContainer>
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ minHeight: "70vh" }}
        >
          <div
            className="text-center"
            style={{ maxWidth: "600px", padding: "40px 20px" }}
          >
            <div
              className="mb-4"
              style={{
                fontSize: "4rem",
                lineHeight: "1",
                background: "linear-gradient(135deg, #3969E7 0%, #2a52cf 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                fontWeight: "700",
              }}
            >
              👋
            </div>
            <h1
              className="mb-3"
              style={{
                color: "#2c3e50",
                fontSize: "2.5rem",
                fontWeight: "600",
                letterSpacing: "-0.5px",
              }}
            >
              Welcome Back!
            </h1>
            <p
              className="mb-4"
              style={{
                color: "#6c757d",
                fontSize: "1.2rem",
                lineHeight: "1.6",
                fontWeight: "400",
              }}
            >
              We're glad to have you here. Explore the system using the
              navigation menu to get started.
            </p>
            <div
              style={{
                marginTop: "2rem",
                padding: "1.5rem",
                backgroundColor: "#f8f9fa",
                borderRadius: "12px",
                border: "1px solid #e9ecef",
              }}
            >
              <p
                className="mb-0"
                style={{
                  color: "#495057",
                  fontSize: "0.95rem",
                  fontStyle: "italic",
                }}
              >
                "Every great journey begins with a single step."
              </p>
            </div>
          </div>
        </div>
      </ListingPageContainer>
    );
  }

  // Show loading state while permissions are being checked
  if (!permissionsLoaded) {
    return (
      <ListingPageContainer>
        <div className="px-3 py-5">
          <div className="text-center" style={{ padding: "60px 20px" }}>
            <p style={{ color: "#666" }}>Loading...</p>
          </div>
        </div>
      </ListingPageContainer>
    );
  }

  return (
    <ListingPageContainer>
      <div className="px-3 py-1">
        <FormHeader title="Dashboard Overview" />

        <DashboardFilters
          dateFrom={dateFrom}
          dateTo={dateTo}
          warehouse={warehouse}
          warehouseOptions={warehouseOptions}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onWarehouseChange={setWarehouse}
          onReset={handleResetFilters}
          stickyEndRef={stickyEndRef}
        />
        
        <TopMetrics
          dateFrom={dateFrom}
          dateTo={dateTo}
          warehouse={warehouse}
        />

        <div className="mb-4">
          <Row>
            <Col xs={12} lg={6} style={{ paddingLeft: "0.375rem", paddingRight: "0.375rem" }}>
              <RevenueBreakdown 
                dateFrom={dateFrom}
                dateTo={dateTo}
                warehouse={warehouse}
              />
            </Col>
            <Col xs={12} lg={6} style={{ paddingLeft: "0.375rem", paddingRight: "0.375rem" }}>
              <RevenueByDeliveryStatus 
                dateFrom={dateFrom}
                dateTo={dateTo}
                warehouse={warehouse}
              />
            </Col>
          </Row>
        </div>

        <QuickActions 
          dateFrom={dateFrom}
          dateTo={dateTo}
          warehouse={warehouse}
        />

        <div className="mb-4">
          <OrderFlowTracker
            dateFrom={dateFrom}
            dateTo={dateTo}
            warehouse={warehouse}
          />
        </div>

        <Row className="align-items-stretch mb-4">
          <Col
            xs={12}
            lg={6}
            className="d-flex mb-3 mb-lg-0"
            style={{ paddingLeft: "0.375rem", paddingRight: "0.375rem" }}
          >
            <EnquirySummary
              dateFrom={dateFrom}
              dateTo={dateTo}
              warehouse={warehouse}
            />
          </Col>
          <Col
            xs={12}
            lg={6}
            className="d-flex"
            style={{ paddingLeft: "0.375rem", paddingRight: "0.375rem" }}
          >
            <QuotationSummary
              dateFrom={dateFrom}
              dateTo={dateTo}
              warehouse={warehouse}
            />
          </Col>
        </Row>

        {/* Sentinel element to mark the end of sticky range */}
        <div ref={stickyEndRef} />

        <div className="mb-4">
          <DispatchSummary
            dateFrom={dateFrom}
            dateTo={dateTo}
            warehouse={warehouse}
          />
        </div>

        <div className="mb-4">
          <InventorySummary />
        </div>

        <div className="mb-4">
          <OrderSummary
            dateFrom={dateFrom}
            dateTo={dateTo}
            warehouse={warehouse}
          />
        </div>
      </div>
    </ListingPageContainer>
  );
};

export default Dashboard;
